import { Button } from "@bcc/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@bcc/components/ui/dialog";
import { Textarea } from "@bcc/components/ui/textarea/textarea";
import { useToast } from "@bcc/hooks/use-toast";
import { getApiErrorMessage } from "@bcc/lib/utils";
import { createLabelPrintBatch } from "@client-shared/services/api/batches";
import type { PurchaseOrder } from "@shared/types";
import { Package, ShoppingBag, Weight } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MAX_PURCHASE_ORDER_CONNECTIONS_LIMIT } from "../constants";
import { getTotalBatchUnits, getTotalBatchWeight, setCreateBatchDialogTestId } from "./create-batch-dialog.utils";

interface CreateBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedOrders: PurchaseOrder[];
  onSuccess: (batchId: string) => void;
  testId?: string;
}

export function CreateBatchDialog({ open, onOpenChange, selectedOrders, onSuccess, testId }: CreateBatchDialogProps) {
  const [batchNotes, setBatchNotes] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const { t } = useTranslation();
  const { toast } = useToast();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset batch notes input when create-batch dialog closes
    if (!open) setBatchNotes("");
  }, [open]);

  const totalOrders = selectedOrders.length;
  const totalUnits = useMemo(() => getTotalBatchUnits(selectedOrders), [selectedOrders]);
  const totalWeight = useMemo(() => getTotalBatchWeight(selectedOrders), [selectedOrders]);
  const exceedsLimit = totalOrders > MAX_PURCHASE_ORDER_CONNECTIONS_LIMIT;

  const handleCreateBatch = useCallback(async () => {
    if (isCreating || exceedsLimit || selectedOrders.length === 0) return;
    setIsCreating(true);
    try {
      const orderIds = selectedOrders.map((order) => order.purchaseOrderId);
      const result = await createLabelPrintBatch({
        orderIds,
        batchNotes: batchNotes || undefined,
      });
      toast({ title: t("views.createBatchDialog.success") });
      onOpenChange(false);
      onSuccess(result.data.batchId);
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, t("views.createBatchDialog.error") as string);
      toast({ title: message, variant: "destructive" });
    } finally {
      setIsCreating(false);
    }
  }, [isCreating, exceedsLimit, selectedOrders, batchNotes, toast, t, onOpenChange, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent testId={testId ?? setCreateBatchDialogTestId("content")} className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t("views.createBatchDialog.title")}</DialogTitle>
          <DialogDescription>{t("views.createBatchDialog.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Batch Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-muted/50 p-4">
              <Package className="h-5 w-5 text-muted-foreground" />
              <div className="text-center">
                <div className="text-2xl font-bold" data-testid={setCreateBatchDialogTestId("orderCount")}>
                  {totalOrders}
                </div>
                <div className="text-xs text-muted-foreground">{t("views.createBatchDialog.orders")}</div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-muted/50 p-4">
              <ShoppingBag className="h-5 w-5 text-muted-foreground" />
              <div className="text-center">
                <div className="text-2xl font-bold" data-testid={setCreateBatchDialogTestId("unitCount")}>
                  {totalUnits}
                </div>
                <div className="text-xs text-muted-foreground">{t("views.createBatchDialog.units")}</div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-muted/50 p-4">
              <Weight className="h-5 w-5 text-muted-foreground" />
              <div className="text-center">
                <div className="text-2xl font-bold" data-testid={setCreateBatchDialogTestId("weightDisplay")}>
                  {t("weight.weightWithSuffix", { grams: totalWeight })}
                </div>
                <div className="text-xs text-muted-foreground">{t("views.createBatchDialog.weight")}</div>
              </div>
            </div>
          </div>

          {exceedsLimit && (
            <p className="text-sm text-destructive" data-testid={setCreateBatchDialogTestId("limitError")}>
              {t("views.createBatchDialog.batchSizeLimitExceeded", {
                max: MAX_PURCHASE_ORDER_CONNECTIONS_LIMIT,
              })}
            </p>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <p className="text-sm font-medium">{t("views.createBatchDialog.notes")}</p>
            <Textarea
              name="batchNotes"
              placeholder={t("views.createBatchDialog.notesPlaceholder")}
              value={batchNotes}
              onChange={(e) => setBatchNotes(e.target.value)}
              testId={setCreateBatchDialogTestId("notesTextarea")}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("views.createBatchDialog.cancel")}
          </Button>
          <Button onClick={handleCreateBatch} disabled={isCreating || exceedsLimit} testId={setCreateBatchDialogTestId("submitButton")}>
            {isCreating ? t("views.createBatchDialog.creating") : t("views.createBatchDialog.createBatch")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
